import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, Send, Loader2 } from "lucide-react";
import { LOGO_URL } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WELCOME =
  "Ciao! Sono l'assistente di copypro3d.it. Posso aiutarti su stampa 3D, materiali, tempi e preventivi. Come posso aiutarti?";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: WELCOME }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setLoading(true);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: sessionRef.current }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          try {
            const data = JSON.parse(line.slice(5).trim());
            if (data.session_id) sessionRef.current = data.session_id;
            if (data.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = {
                  role: "assistant",
                  content: copy[copy.length - 1].content + data.delta,
                };
                return copy;
              });
            }
            if (data.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: data.error };
                return copy;
              });
            }
          } catch { /* ignore partial */ }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Errore di connessione. Riprova tra poco." };
        return copy;
      });
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Launcher */}
      <motion.button
        data-testid="chat-launcher"
        onClick={() => setOpen((v) => !v)}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.2, type: "spring", stiffness: 200 }}
        className="fixed bottom-6 right-6 z-[60] h-14 w-14 rounded-full bg-[#00C8D7] text-[#0A0E14] flex items-center justify-center shadow-lg shadow-[#00C8D7]/30 hover:scale-105 transition-transform"
        aria-label="Apri chat assistente"
      >
        {open ? <X size={24} /> : <MessageSquare size={24} />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-6 z-[60] w-[calc(100vw-3rem)] sm:w-96 h-[520px] max-h-[70vh] bg-[#111827] border border-white/10 rounded-lg shadow-2xl flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10 bg-[#0A0E14]">
              <img src={LOGO_URL} alt="copypro3d.it" className="h-9 w-9 rounded-full object-cover" />
              <div className="flex-1">
                <h3 className="font-display font-bold text-sm tracking-tight text-white">Assistente CopyPro</h3>
                <span className="font-mono-tech text-[10px] text-[#00C8D7] flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00C8D7] animate-pulse" /> Online
                </span>
              </div>
              <button data-testid="chat-close" onClick={() => setOpen(false)} className="text-slate-400 hover:text-white">
                <X size={18} />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-[#00C8D7] text-[#0A0E14] rounded-2xl rounded-br-sm"
                        : "bg-[#0A0E14] text-slate-200 border border-white/10 rounded-2xl rounded-bl-sm"
                    }`}
                  >
                    {m.content || (loading && i === messages.length - 1 ? <Loader2 size={16} className="animate-spin" /> : "")}
                  </div>
                </div>
              ))}
            </div>

            {/* Input */}
            <div className="border-t border-white/10 p-3 bg-[#0A0E14]">
              <div className="flex items-end gap-2">
                <textarea
                  data-testid="chat-input"
                  rows={1}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKey}
                  placeholder="Scrivi un messaggio..."
                  className="flex-1 resize-none bg-[#111827] border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#00C8D7] transition-colors max-h-24"
                />
                <button
                  data-testid="chat-send"
                  onClick={send}
                  disabled={loading || !input.trim()}
                  className="h-10 w-10 shrink-0 rounded-lg bg-[#00C8D7] text-[#0A0E14] flex items-center justify-center hover:bg-[#00C8D7]/85 transition-colors disabled:opacity-50"
                >
                  {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
